import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sites } from './Sites.js';
import { BrandsTab } from './BrandsTab.js';
import { OwnApprovalPanel } from './OwnApprovalPanel.js';
import { SeImport } from './SeImport.js';
import { SeSync } from './SeSync.js';
import { EshopImport } from './EshopImport.js';
import { EnvironmentTab } from './EnvironmentTab.js';
import { BuildTag } from '../components/BuildTag.js';
import { McpTokensTab } from './McpTokensTab.js';
import { LayersTab } from './LayersTab.js';
import { MonitoringTab } from './MonitoringTab.js';
import { WebSettings } from './WebSettings.js';
import { Ga4Card } from './Ga4Card.js';
import { api } from '../lib/api.js';
import type { Me, Rights } from '../lib/session.js';
import { DEPARTMENTS, DEPARTMENT_LABEL } from '@hub/shared';
import { ENVIRONMENT_LABEL, SEND_MODE_LABEL, label } from '../lib/labels.js';
import { ENV_META } from '../lib/environments.js';
import { dateTime, num } from '../lib/format.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  Panel,
  StatusBadge,
  TabBar,
  Tile,
} from '../components/ui.js';
import type { IconName } from '../components/ui.js';
import { EnvScope, Steps, activeEnv } from '../components/2c-parts.js';
import '../screens-2c.css';

interface SettingsData {
  values: Record<string, unknown>;
  sendMode: {
    stored: string;
    ceiling: string;
    effective: string;
    available: string[];
  };
  connections: {
    eshopIngestKeys: string[];
    webEventsEnabled: boolean;
    watchdogPublicEnabled: boolean;
    feedUrl: string | null;
    smartEmailingConfigured: boolean;
    clockMode: string;
    appEnv: string;
  };
}

type Tab =
  | 'send'
  | 'brands'
  | 'rules'
  | 'connections'
  | 'imports'
  | 'users'
  | 'mcp'
  | 'audit'
  | 'environment'
  | 'layers'
  | 'monitoring';

/** Co se na záložce mění; zobrazí se v pruhu s prostředím nad obsahem. */
const TAB_SCOPE: Record<Exclude<Tab, 'environment'>, string> = {
  send: 'Režim odesílání se mění jen tady a jen v tomto prostředí.',
  brands: 'Značky, odesílatelé a odkazy platí jen v tomto prostředí.',
  rules: 'Pravidla ovlivňují, kolik zpráv komu odejde a jak dlouho se data uchovávají.',
  connections: 'Přístupy ke SmartEmailingu a napojení se ukládají zvlášť pro každé prostředí.',
  imports: 'Ostrý import zapisuje do databáze tohoto prostředí. Zkouška nezapisuje nic.',
  users: 'Uživatelé a jejich oddělení jsou v každém prostředí samostatní.',
  mcp: 'Tokeny pro MCP platí jen v tomto prostředí; každé prostředí má vlastní.',
  audit: 'Zobrazuje se jen historie změn tohoto prostředí.',
  layers: 'Zapnutí vlny platí jen v tomto prostředí; každé prostředí má vlastní.',
  monitoring:
    'Upozornění, prahy a ztlumení platí pro toto prostředí; kanály se nastavují v prostředí serveru.',
};

export function Settings({ me, rights }: { me: Me; rights: Rights }): JSX.Element {
  const [tab, setTab] = useState<Tab>('send');
  const canWrite = rights.write('settings');
  const showEnv = Boolean(me.environment) && rights.can('switch_environment');

  const tabs: Array<{ id: Tab; label: string; hint: string; icon: IconName }> = [
    ...(showEnv
      ? [
          {
            id: 'environment' as const,
            label: 'Prostředí',
            hint: 'Vývoj, Zkouška, Produkce',
            icon: 'layers' as const,
          },
        ]
      : []),
    ...(rights.can('manage_layers')
      ? [
          {
            id: 'layers' as const,
            label: 'Vlny nasazení',
            hint: 'Co hub právě dělá',
            icon: 'layers' as const,
          },
        ]
      : []),
    { id: 'monitoring', label: 'Dohled', hint: 'Upozornění na výpadek', icon: 'settings' },
    { id: 'send', label: 'Odesílání', hint: 'Co smí hub odeslat', icon: 'settings' },
    { id: 'brands', label: 'Značky', hint: 'Odesílatelé a odkazy', icon: 'star' },
    { id: 'rules', label: 'Pravidla', hint: 'Limity zpráv a uchování dat', icon: 'settings' },
    { id: 'connections', label: 'Napojení', hint: 'SmartEmailing, e-shop, web', icon: 'plug' },
    { id: 'imports', label: 'Importy', hint: 'Data z e-shopu, SE a souborů', icon: 'plug' },
    { id: 'users', label: 'Uživatelé', hint: 'Účty a oddělení', icon: 'star' },
    ...(rights.can('manage_users')
      ? [
          {
            id: 'mcp' as const,
            label: 'Přístup pro MCP',
            hint: 'Tokeny pro Claude',
            icon: 'plug' as const,
          },
        ]
      : []),
    { id: 'audit', label: 'Audit změn', hint: 'Kdo, kdy a co změnil', icon: 'settings' },
  ];

  return (
    <>
      <PageHeader
        title="Nastavení"
        description="Co smí hub odesílat, s čím je napojený a kdo ho spravuje. Každé prostředí (Vývoj, Zkouška, Produkce) má vlastní databázi i vlastní nastavení."
      />
      <div className="s2c-tabs">
        <TabBar tabs={tabs} value={tab} onChange={setTab} />
      </div>
      {tab !== 'environment' ? <EnvScope me={me}>{TAB_SCOPE[tab]}</EnvScope> : null}

      {tab === 'send' ? <SendModeTab me={me} rights={rights} /> : null}
      {tab === 'send' ? <OwnApprovalPanel canChange={rights.can('change_send_mode')} /> : null}
      {tab === 'brands' ? <BrandsTab canWrite={canWrite} /> : null}
      {tab === 'rules' ? <RulesTab canWrite={canWrite} me={me} /> : null}
      {tab === 'connections' ? (
        <ConnectionsTab canWrite={canWrite} rights={rights} me={me} />
      ) : null}
      {tab === 'imports' ? <ImportsHub me={me} rights={rights} canWrite={canWrite} /> : null}
      {tab === 'users' ? <UsersTab canManage={rights.can('manage_users')} /> : null}
      {tab === 'mcp' ? <McpTokensTab meId={me.user.id} /> : null}
      {tab === 'audit' ? <AuditTab me={me} /> : null}
      {tab === 'environment' || tab === 'monitoring' ? (
        <p className="note">
          <BuildTag long build={me.build} /> (hash a zpráva commitu jsou v nápovědě po najetí myší)
        </p>
      ) : null}
      {tab === 'environment' ? <EnvironmentTab me={me} /> : null}
      {tab === 'layers' ? <LayersTab me={me} /> : null}
      {tab === 'monitoring' ? <MonitoringTab canWrite={canWrite} /> : null}
    </>
  );
}

function useSettings() {
  return useQuery<SettingsData>({
    queryKey: ['settings'],
    queryFn: () => api.get('/api/settings'),
  });
}

const MODES = [
  {
    id: 'dry_run',
    text: 'Hub rozhoduje a zapisuje, co by odeslal, ale nic neodešle. Bezpečný výchozí stav.',
  },
  {
    id: 'test_allowlist',
    text: 'Zprávy odcházejí jen na zkušební adresy z povoleného seznamu. Zákazníci nedostanou nic.',
  },
  {
    id: 'live',
    text: 'Zprávy odcházejí skutečným zákazníkům. Zapínejte až po týdnu na zkušebních adresách.',
  },
] as const;

function SendModeTab({ me, rights }: { me: Me; rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const settings = useSettings();
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: (input: { mode: string; confirmation?: string }) =>
      api.post('/api/settings/send-mode', input),
    onSuccess: () => {
      setError(null);
      setAsking(null);
      void client.invalidateQueries({ queryKey: ['settings'] });
      void client.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err: Error) => {
      setAsking(null);
      setError(err.message);
    },
  });

  if (settings.isLoading) return <LoadingState what="nastavení" />;
  if (settings.isError)
    return (
      <ErrorState
        error={settings.error}
        title="Nastavení se nepodařilo načíst"
        onRetry={() => void settings.refetch()}
      />
    );
  const data = settings.data;
  if (!data) return <LoadingState />;

  const canChange = rights.can('change_send_mode');
  const liveBlocked = data.sendMode.ceiling !== 'live';
  const envName = ENV_META[activeEnv(me)].label;
  const askingLive = asking === 'live';

  return (
    <>
      {error ? <ErrorState error={new Error(error)} title="Režim se nepodařilo změnit" /> : null}
      <Panel
        title="Režim odesílání"
        subtitle="Určuje, jestli zprávy opravdu odcházejí. Bezpečný výchozí stav je Nanečisto."
      >
        <dl className="kv">
          <dt>Účinný režim</dt>
          <dd data-testid="effective-mode">
            <b>{label(SEND_MODE_LABEL, data.sendMode.effective)}</b>
          </dd>
          <dt>Nastavení v administraci</dt>
          <dd>{label(SEND_MODE_LABEL, data.sendMode.stored)}</dd>
          <dt>Horní mez prostředí</dt>
          <dd>
            {label(SEND_MODE_LABEL, data.sendMode.ceiling)}{' '}
            <span className="muted">
              {me.environment
                ? `(strop prostředí ${ENVIRONMENT_LABEL[me.environment.name]})`
                : '(nastavuje správce serveru)'}
            </span>
          </dd>
        </dl>

        {liveBlocked ? (
          <div className="s2c-warn" data-testid="live-blocked">
            Prostředí dovoluje nejvýš <b>{label(SEND_MODE_LABEL, data.sendMode.ceiling)}</b>.{' '}
            {me.environment ? (
              <>
                Ostrý režim z administrace zapnout nejde. Dává ho jen prostředí Produkce a to musí
                správce serveru povolit (<code>HUB_ENVIRONMENTS_ALLOWED</code>).
              </>
            ) : (
              <>
                Ostrý režim z administrace zapnout nejde, musí ho povolit správce serveru (proměnná{' '}
                <code>SEND_MODE</code>).
              </>
            )}
          </div>
        ) : null}

        {!canChange ? <p className="note">Režim odesílání smí měnit jen administrátor.</p> : null}

        <div className="s2c-modecards">
          {MODES.map((m) => {
            const current = m.id === data.sendMode.stored;
            const blocked = !data.sendMode.available.includes(m.id);
            const reason = blocked
              ? `Prostředí dovoluje nejvýš ${label(SEND_MODE_LABEL, data.sendMode.ceiling)}.`
              : undefined;
            return (
              <div key={m.id} className={`s2c-modecard${current ? ' on' : ''}`}>
                <h3>
                  {label(SEND_MODE_LABEL, m.id)}
                  {current ? (
                    <StatusBadge tone="acc" icon="check">
                      Nastavený
                    </StatusBadge>
                  ) : null}
                </h3>
                <p>{m.text}</p>
                {canChange && !current ? (
                  <>
                    <button
                      type="button"
                      className={`btn${m.id === 'live' ? ' dng' : ''}`}
                      disabled={blocked || change.isPending}
                      title={reason}
                      onClick={() => setAsking(m.id)}
                    >
                      Přepnout na {label(SEND_MODE_LABEL, m.id)}
                    </button>
                    {reason ? <span className="s2c-reason">{reason}</span> : null}
                  </>
                ) : null}
              </div>
            );
          })}
        </div>

        <dl className="kv s2c-sub">
          <dt>Hodiny</dt>
          <dd>
            {data.connections.clockMode === 'simulated' ? 'Simulované (jen zkoušení)' : 'Skutečné'}
          </dd>
          <dt>Časové pásmo</dt>
          <dd>{me.defaultTimezone}</dd>
        </dl>
      </Panel>

      <ConfirmDialog
        open={asking !== null}
        title={`Přepnout na režim ${asking ? label(SEND_MODE_LABEL, asking) : ''}?`}
        confirmLabel={`Přepnout na ${asking ? label(SEND_MODE_LABEL, asking) : ''}`}
        danger={askingLive}
        requireWord={askingLive ? 'OSTRÝ' : undefined}
        busy={change.isPending}
        onCancel={() => setAsking(null)}
        onConfirm={(word) => {
          if (asking) change.mutate({ mode: asking, ...(word ? { confirmation: word } : {}) });
        }}
      >
        <p>
          Měníte režim odesílání v prostředí <b>{envName}</b>. Ostatní prostředí zůstanou, jak jsou.
        </p>
        {askingLive ? (
          <p>
            Ostrý režim znamená, že zprávy odcházejí zákazníkům. Nejdřív doporučujeme týden na
            testovacích adresách.
          </p>
        ) : null}
      </ConfirmDialog>
    </>
  );
}

const RULE_GROUPS: Array<{
  title: string;
  lead: string;
  rules: Array<{ key: string; title: string; hint: string; unit: string; max?: number }>;
}> = [
  {
    title: 'Kolik zpráv komu odejde',
    lead: 'Vyšší hodnoty znamenají méně zpráv. Při pochybnosti nechte přísnější hodnotu.',
    rules: [
      {
        key: 'frequency_cap_days',
        title: 'Frekvenční limit',
        hint: 'Kolik dnů musí uplynout mezi dvěma marketingovými zprávami jednomu člověku.',
        unit: 'dny',
      },
      {
        key: 'merge_window_hours',
        title: 'Okno sloučení',
        hint: 'V kolika hodinách se zprávy ke stejné události slučují do jedné.',
        unit: 'hodiny',
      },
      {
        key: 'support_pause_days',
        title: 'Pauza po kontaktu s podporou',
        hint: 'Kolik dnů po dotazu na podporu člověk nedostane marketing.',
        unit: 'dny',
      },
      {
        key: 'holdout_percent',
        title: 'Kontrolní skupina',
        hint: 'Kolik procent kontaktů záměrně marketing nedostane, aby šlo změřit jeho přínos.',
        unit: '%',
        max: 100,
      },
      {
        key: 'watchdog_pending_days',
        title: 'Platnost nepotvrzeného hlídacího psa',
        hint: 'Za kolik dnů vyprší hlídání, které zákazník nepotvrdil.',
        unit: 'dny',
      },
    ],
  },
  {
    title: 'Rozesílky',
    lead: 'Rozesílky se uvolňují po krocích 1 % → 10 % → 100 %. Přísnější hodnoty znamenají dřívější zastavení.',
    rules: [
      {
        key: 'broadcast_batch_size',
        title: 'Dávka rozesílky',
        hint: 'Kolik příjemců zpracuje rozesílka za minutu (omezení rychlosti). 50 je opatrná hodnota; zvyšujte ji až po ověření limitů SmartEmailingu.',
        unit: 'příjemců',
        max: 10000,
      },
      {
        key: 'broadcast_send_group_size',
        title: 'Příjemců v jednom volání',
        hint: 'Kolik příjemců se odešle jedním voláním SmartEmailingu (dokumentace dovoluje až 500). 1 = po jednom jako dosud (opatrné). Při větší hodnotě se výsledek vyhodnocuje po příjemcích; chyba celého volání znamená chybu odeslání u všech v dané skupině a nic se samo neopakuje.',
        unit: 'příjemců',
        max: 500,
      },
      {
        key: 'broadcast_error_threshold_percent',
        title: 'Práh odskoků a chyb',
        hint: 'Od jakého podílu odskoků a chyb odeslání se rozesílka sama zastaví.',
        unit: '%',
        max: 100,
      },
      {
        key: 'broadcast_min_sample',
        title: 'Minimální vzorek pro práh',
        hint: 'Od kolika odeslaných zpráv se míra odskoků a chyb vyhodnocuje.',
        unit: 'zpráv',
      },
      {
        key: 'broadcast_step_wait_minutes',
        title: 'Čekání na reakce mezi kroky',
        hint: 'Kolik minut po dokončení kroku lze nejdřív potvrdit další krok.',
        unit: 'minuty',
      },
      {
        key: 'broadcast_deadline_hours',
        title: 'Lhůta rozesílky',
        hint: 'Po kolika hodinách od začátku propadnou příjemci, kteří čekají na dočasný důvod (frekvenční strop, okno).',
        unit: 'hodiny',
      },
    ],
  },
  {
    title: 'Jak dlouho se data uchovávají',
    lead: 'Po uplynutí lhůty se data smažou natrvalo. Hodnota 0 znamená, že se nemažou nikdy. Zkrácení lhůty smaže starší data při nejbližším úklidu.',
    rules: [
      {
        key: 'retention_web_events_months',
        title: 'Události a návštěvy z webu',
        hint: 'Chování návštěvníků na webu včetně návštěv po e-mailu. Po lhůtě zůstanou jen anonymní součty.',
        unit: 'měsíce',
      },
      {
        key: 'retention_message_payload_months',
        title: 'Obsah zpráv',
        hint: 'Text odeslaných zpráv.',
        unit: 'měsíce',
      },
      {
        key: 'retention_inbox_events_months',
        title: 'Surové události',
        hint: 'Původní zprávy přijaté z e-shopu.',
        unit: 'měsíce',
      },
      {
        key: 'retention_care_notes_months',
        title: 'Poznámky a řešení v péči o zákazníky',
        hint: 'Interní poznámky k případům.',
        unit: 'měsíce',
      },
      {
        key: 'retention_support_months',
        title: 'Metadata dotazů podpory',
        hint: 'Bez textu dotazů.',
        unit: 'měsíce',
      },
      {
        key: 'retention_watchdog_proof_months',
        title: 'Důkaz souhlasu hlídacího psa',
        hint: 'IP adresa a prohlížeč, kterými se souhlas doložil.',
        unit: 'měsíce',
      },
      {
        key: 'retention_candidates_months',
        title: 'Kandidáti zpráv',
        hint: 'Zprávy, které hub zvažoval odeslat.',
        unit: 'měsíce',
      },
    ],
  },
];

function RulesTab({ canWrite, me }: { canWrite: boolean; me: Me }): JSX.Element {
  const client = useQueryClient();
  const settings = useSettings();
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch('/api/settings', body),
    onSuccess: () => {
      setError(null);
      setDraft({});
      setConfirming(false);
      void client.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (err: Error) => {
      setConfirming(false);
      setError(err.message);
    },
  });

  if (settings.isLoading) return <LoadingState what="pravidla" />;
  if (settings.isError)
    return (
      <ErrorState
        error={settings.error}
        title="Pravidla se nepodařilo načíst"
        onRetry={() => void settings.refetch()}
      />
    );
  const values = settings.data?.values ?? {};
  const all = RULE_GROUPS.flatMap((g) => g.rules);
  const problem = (r: { key: string; max?: number }): string | null => {
    const raw = draft[r.key];
    if (raw === undefined) return null;
    const n = Number(raw);
    if (raw.trim() === '' || !Number.isInteger(n) || n < 0) return 'Zadejte celé číslo od 0.';
    if (r.max !== undefined && n > r.max) return `Nejvýš ${r.max}.`;
    return null;
  };
  const changed = all.filter((r) => draft[r.key] !== undefined);
  const invalid = changed.some((r) => problem(r) !== null);
  const envName = ENV_META[activeEnv(me)].label;

  return (
    <>
      {error ? <ErrorState error={new Error(error)} title="Pravidla se nepodařilo uložit" /> : null}
      <PageHeader
        level={2}
        title="Pravidla"
        description="Číselné limity, kterými se hub řídí. Katalog zpráv je soubor v repozitáři a mění se nasazením, ne tady."
      />
      {RULE_GROUPS.map((g) => (
        <Panel key={g.title} title={g.title} subtitle={g.lead}>
          <div className="s2c-form">
            {g.rules.map((r) => (
              <Field key={r.key} label={`${r.title} (${r.unit})`} hint={r.hint} error={problem(r)}>
                <input
                  type="number"
                  min={0}
                  max={r.max}
                  step={1}
                  inputMode="numeric"
                  className="s2c-num"
                  value={draft[r.key] ?? String(values[r.key] ?? '')}
                  disabled={!canWrite}
                  aria-invalid={problem(r) !== null}
                  onChange={(e) => setDraft({ ...draft, [r.key]: e.target.value })}
                />
              </Field>
            ))}
          </div>
        </Panel>
      ))}

      <WebSettings canWrite={canWrite} values={values} envName={envName} />

      {canWrite ? (
        <div className="s2c-actions">
          <button
            type="button"
            className="btn pri"
            disabled={changed.length === 0 || invalid || patch.isPending}
            onClick={() => setConfirming(true)}
          >
            Uložit změny{changed.length > 0 ? ` (${changed.length})` : ''}
          </button>
          <button
            type="button"
            className="btn"
            disabled={changed.length === 0}
            onClick={() => setDraft({})}
          >
            Zahodit změny
          </button>
        </div>
      ) : (
        <p className="note">Pravidla smí měnit jen správce.</p>
      )}

      <details className="s2c-details">
        <summary>Časová okna (jen pro čtení)</summary>
        <p className="note">Okna plánování zpráv po nákupu a okno závodu. Mění se nasazením.</p>
        <pre className="code">{JSON.stringify(values.windows ?? {}, null, 2)}</pre>
        <pre className="code">{JSON.stringify(values.race_window ?? {}, null, 2)}</pre>
      </details>

      <ConfirmDialog
        open={confirming}
        title="Uložit změněná pravidla?"
        confirmLabel="Uložit pravidla"
        busy={patch.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() =>
          patch.mutate(Object.fromEntries(changed.map((r) => [r.key, Number(draft[r.key])])))
        }
      >
        <p>
          Pravidla se změní v prostředí <b>{envName}</b>:
        </p>
        <ul>
          {changed.map((r) => (
            <li key={r.key}>
              {r.title}: {String(values[r.key] ?? '—')} → <b>{draft[r.key]}</b> {r.unit}
            </li>
          ))}
        </ul>
        {changed.some((r) => r.key.startsWith('retention_')) ? (
          <p>Zkrácená lhůta uchování smaže starší data natrvalo.</p>
        ) : null}
      </ConfirmDialog>
    </>
  );
}

interface CredentialsData {
  accounts: Array<{
    brand: string | null;
    username: string;
    keyHint: string;
    baseUrl: string | null;
    lastCheckAt: string | null;
    lastCheckOk: boolean | null;
    lastCheckError: string | null;
  }>;
  defaultSource: 'db' | 'env' | 'none';
  appEnv: string;
}

/** Jeden účet SmartEmailingu (TicketsGP) pro všechny značky; klíč se po uložení už nikdy nezobrazí (D-102). */
function SmartEmailingCard({ canWrite, me }: { canWrite: boolean; me: Me }): JSX.Element {
  const client = useQueryClient();
  const [username, setUsername] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const query = useQuery<CredentialsData>({
    queryKey: ['provider-credentials'],
    queryFn: () => api.get('/api/provider/credentials'),
  });
  const account = query.data?.accounts.find((a) => a.brand === null) ?? null;
  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['provider-credentials'] });
    void client.invalidateQueries({ queryKey: ['brands'] });
  };
  const fail = (err: Error): void => {
    setNotice(null);
    setError(err.message);
  };
  const save = useMutation({
    mutationFn: () =>
      api.put('/api/provider/credentials', {
        username: username.trim() || account?.username,
        ...(apiKey ? { apiKey } : {}),
      }),
    onSuccess: () => {
      setError(null);
      setNotice('Uloženo.');
      setApiKey('');
      setUsername('');
      refresh();
    },
    onError: fail,
  });
  const check = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>('/api/provider/credentials/check', {}),
    onSuccess: (r) => {
      setError(null);
      setNotice(r.ok ? 'Přístupy fungují.' : null);
      refresh();
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => api.del('/api/provider/credentials'),
    onSuccess: () => {
      setError(null);
      setRemoving(false);
      setNotice('Přístupy odebrány.');
      refresh();
    },
    onError: (err: Error) => {
      setRemoving(false);
      fail(err);
    },
  });

  const chip = !account ? (
    query.data?.defaultSource === 'env' ? (
      <StatusBadge tone="neu">Napojeno z nastavení serveru</StatusBadge>
    ) : (
      <StatusBadge tone="neu">Nenapojeno</StatusBadge>
    )
  ) : account.lastCheckOk === false ? (
    <StatusBadge tone="bad" icon="alert">
      Chyba spojení
    </StatusBadge>
  ) : (
    <StatusBadge tone="ok" icon="check">
      Napojeno
    </StatusBadge>
  );

  return (
    <Panel
      title="SmartEmailing"
      subtitle="Služba, přes kterou hub rozesílá e-maily. Jeden účet slouží všem značkám; značka určuje jen šablonu a odesílatele."
      action={chip}
    >
      <EnvScope me={me} compact>
        Přístupy uložené tady platí jen pro toto prostředí.
      </EnvScope>
      {error ? <ErrorState error={new Error(error)} title="Přístupy se nepodařilo uložit" /> : null}
      {notice ? (
        <p className="note" role="status">
          {notice}
        </p>
      ) : null}
      <p className="note">
        API klíč se ukládá šifrovaně v databázi hubu a nikdy se nevrací zpět do administrace. Po
        uložení ho už nikdo neuvidí; při ztrátě se zadá znovu.
      </p>
      <div className="s2c-warn" role="note">
        <strong>Pozor na odhlášení.</strong> SmartEmailing vyhodnocuje odhlášení a blokaci za celý
        účet. Odhlášení z e-mailu jedné značky tak může zablokovat kontakt u všech značek. Hub
        eviduje souhlas po značkách, ale SmartEmailing doručení ostatních značek takovému kontaktu
        odmítne. Ověřuje se u podpory SmartEmailingu (otevřená otázka O-2).
      </div>
      {canWrite ? (
        <>
          <div className="s2c-form">
            <Field
              label="Uživatelské jméno (e-mail účtu)"
              hint={account ? `Uloženo: ${account.username}. Prázdné pole ho ponechá.` : undefined}
            >
              <input
                type="text"
                autoComplete="off"
                placeholder={account ? account.username : 'uzivatel@example.invalid'}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </Field>
            <Field
              label="API klíč"
              hint={
                account
                  ? `Uložený klíč končí na ${account.keyHint}. Prázdné pole ho ponechá.`
                  : 'Najdete ho v nastavení účtu u SmartEmailingu.'
              }
            >
              <input
                type="password"
                autoComplete="off"
                placeholder={account ? `•••• ${account.keyHint}` : ''}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
            </Field>
          </div>
          <div className="s2c-actions">
            <button
              type="button"
              className="btn pri"
              onClick={() => save.mutate()}
              disabled={save.isPending || (!account && (!username.trim() || !apiKey))}
            >
              Uložit přístupy
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => check.mutate()}
              disabled={!account || check.isPending}
            >
              {check.isPending ? 'Ověřuji…' : 'Ověřit přístupy'}
            </button>
            {account ? (
              <button type="button" className="btn dng" onClick={() => setRemoving(true)}>
                Odebrat přístupy
              </button>
            ) : null}
          </div>
        </>
      ) : (
        <p className="note">Přístupy smí měnit jen správce.</p>
      )}
      {account ? (
        <p className="note">
          Účet <code>{account.username}</code>, klíč končí na <code>{account.keyHint}</code>.{' '}
          {account.lastCheckAt
            ? account.lastCheckOk
              ? `Ověřeno ${dateTime(account.lastCheckAt)}: v pořádku.`
              : `Ověřeno ${dateTime(account.lastCheckAt)}: ${account.lastCheckError ?? 'chyba'}`
            : 'Zatím neověřeno.'}{' '}
          Změna se v odesílání projeví nejpozději do minuty.
        </p>
      ) : query.data?.defaultSource === 'env' ? (
        <p className="note">
          Zatím se používají přístupy ze serveru (<code>SE_USERNAME</code>, <code>SE_API_KEY</code>
          ); přístupy uložené v administraci mají přednost.
        </p>
      ) : (
        <p className="note">Bez přístupů hub nic neodešle; používá zkušební poskytovatele.</p>
      )}
      <ConfirmDialog
        open={removing}
        title="Odebrat přístupy ke SmartEmailingu?"
        confirmLabel="Odebrat přístupy"
        danger
        busy={remove.isPending}
        onCancel={() => setRemoving(false)}
        onConfirm={() => remove.mutate()}
      >
        <p>
          Přístupy se odeberou jen z prostředí <b>{ENV_META[activeEnv(me)].label}</b>. Odesílání v
          něm se zastaví, dokud je nezadáte znovu.
        </p>
      </ConfirmDialog>
    </Panel>
  );
}

function ConnectionsTab({
  canWrite,
  rights,
  me,
}: {
  canWrite: boolean;
  rights: Rights;
  me: Me;
}): JSX.Element {
  const settings = useSettings();
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const check = useMutation({
    mutationFn: () => api.post('/api/provider/capability-check'),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['capability'] });
      void client.invalidateQueries({ queryKey: ['data-quality'] });
    },
    onError: (err: Error) => setError(err.message),
  });
  const report = useQuery<{
    report: {
      items: Array<{ key: string; label: string; status: string; detail?: string }>;
    } | null;
    checkedAt: string | null;
  }>({
    queryKey: ['capability'],
    queryFn: () => api.get('/api/provider/capability-check'),
  });

  if (settings.isLoading) return <LoadingState what="napojení" />;
  if (settings.isError)
    return (
      <ErrorState
        error={settings.error}
        title="Napojení se nepodařilo načíst"
        onRetry={() => void settings.refetch()}
      />
    );
  const data = settings.data;
  if (!data) return <LoadingState />;

  return (
    <>
      {error ? <ErrorState error={new Error(error)} title="Kontrola se nepodařila" /> : null}
      <Sites rights={rights} />

      <SmartEmailingCard canWrite={canWrite} me={me} />

      <Ga4Card canWrite={canWrite} />

      <Panel
        title="Napojení na e-shop a web"
        subtitle="Zapínají se na serveru, ne z administrace, aby je nikdo nezapnul omylem."
      >
        <dl className="kv">
          <dt>Klíče pro události z e-shopu</dt>
          <dd>
            {data.connections.eshopIngestKeys.length === 0 ? (
              <StatusBadge tone="warn" icon="alert">
                žádné
              </StatusBadge>
            ) : (
              data.connections.eshopIngestKeys.map((s) => <code key={s}>{s} </code>)
            )}
          </dd>
          <dt>Události z webu</dt>
          <dd>{data.connections.webEventsEnabled ? 'zapnuté' : 'vypnuté (hub je nepřijímá)'}</dd>
          <dt>Veřejný hlídací pes</dt>
          <dd>
            {data.connections.watchdogPublicEnabled
              ? 'zapnutý'
              : 'vypnutý (veřejná stránka je nedostupná)'}
          </dd>
          <dt>Stahování dostupnosti</dt>
          <dd>{data.connections.feedUrl ?? 'bez adresy, stahování neběží'}</dd>
          <dt>SmartEmailing ze serveru</dt>
          <dd>
            {data.connections.smartEmailingConfigured
              ? 'přístupové údaje na serveru jsou nastavené (záložní cesta)'
              : 'na serveru nic; přístupy se zadávají v kartě SmartEmailing výše'}
          </dd>
        </dl>
        <p className="note">
          Technické názvy nastavení na serveru: <code>WEB_EVENTS_ENABLED</code>,{' '}
          <code>WATCHDOG_PUBLIC_ENABLED</code>, <code>FEED_URL</code>.
        </p>
      </Panel>

      <Panel
        title="Kontrola schopností SmartEmailingu"
        subtitle="Jen čte: ověří, co daný účet SmartEmailingu dovoluje. Nic nezapisuje."
        action={
          canWrite ? (
            <button
              type="button"
              className="btn pri"
              onClick={() => check.mutate()}
              disabled={check.isPending}
            >
              {check.isPending ? 'Ověřuji…' : 'Ověřit teď'}
            </button>
          ) : null
        }
      >
        {report.isError ? (
          <ErrorState
            error={report.error}
            title="Výsledek kontroly se nepodařilo načíst"
            onRetry={() => void report.refetch()}
          />
        ) : null}
        {report.isLoading ? <LoadingState what="výsledek kontroly" /> : null}
        {report.data?.checkedAt ? (
          <p className="note">Naposledy ověřeno {dateTime(report.data.checkedAt)}.</p>
        ) : null}
        {report.data?.report ? (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Bod</th>
                  <th>Výsledek</th>
                  <th>Podrobnost</th>
                </tr>
              </thead>
              <tbody>
                {report.data.report.items.map((item) => (
                  <tr key={item.key}>
                    <td>{item.label}</td>
                    <td>
                      {item.status === 'ok' ? (
                        <StatusBadge tone="ok" icon="check">
                          v pořádku
                        </StatusBadge>
                      ) : item.status === 'failed' ? (
                        <StatusBadge tone="bad" icon="alert">
                          nefunguje
                        </StatusBadge>
                      ) : (
                        <StatusBadge tone="warn">ruční krok</StatusBadge>
                      )}
                    </td>
                    <td className="muted">{item.detail ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : report.isSuccess ? (
          <EmptyState title="Kontrola zatím neproběhla" icon="info">
            Spusťte ji před zapnutím odesílání, ať víte, co účet SmartEmailingu dovoluje.
          </EmptyState>
        ) : null}
        <p className="note">
          Otevřené otázky k rozhraní SmartEmailingu jsou v <code>docs/se-api-notes.md</code>.
        </p>
      </Panel>
    </>
  );
}

type ImportSub = 'imp_sync' | 'imp_eshop' | 'imp_se' | 'imp_file';

function ImportsHub({
  me,
  rights,
  canWrite,
}: {
  me: Me;
  rights: Rights;
  canWrite: boolean;
}): JSX.Element {
  const [sub, setSub] = useState<ImportSub>('imp_sync');
  const tabs: Array<{ id: ImportSub; label: string; hint: string; icon: IconName }> = [
    {
      id: 'imp_sync',
      label: 'Synchronizace SE',
      hint: 'Automatické stahování ze SmartEmailingu',
      icon: 'refresh',
    },
    {
      id: 'imp_eshop',
      label: 'Z e-shopu',
      hint: 'Historie zákazníků a objednávek',
      icon: 'upload',
    },
    {
      id: 'imp_se',
      label: 'Ze SmartEmailingu',
      hint: 'Seznamy a kontakty (jen čtení)',
      icon: 'mail',
    },
    { id: 'imp_file', label: 'Ze souboru', hint: 'CSV nebo JSON', icon: 'file' },
  ];
  return (
    <>
      <div className="s2c-tabs sub">
        <TabBar tabs={tabs} value={sub} onChange={setSub} />
      </div>
      {sub === 'imp_sync' ? <SeSync me={me} rights={rights} /> : null}
      {sub === 'imp_eshop' ? <EshopImport me={me} rights={rights} /> : null}
      {sub === 'imp_se' ? <SeImport me={me} rights={rights} /> : null}
      {sub === 'imp_file' ? <ImportsTab canWrite={canWrite} /> : null}
    </>
  );
}

const KIND_LABEL: Record<string, string> = {
  races: 'Závody',
  orders: 'Objednávky',
  contacts: 'Zákazníci',
  consents: 'Souhlasy',
  gifts: 'Dárkové poukazy',
  availability: 'Dostupnost vstupenek',
  watchdogs: 'Hlídací psi',
};

function ImportsTab({ canWrite }: { canWrite: boolean }): JSX.Element {
  const client = useQueryClient();
  const [kind, setKind] = useState('races');
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);

  const list = useQuery<{
    items: Array<{
      id: string;
      kind: string;
      filename: string | null;
      rows_total: number;
      rows_ok: number;
      rows_failed: number;
      at: string;
    }>;
    kinds: string[];
  }>({
    queryKey: ['imports'],
    queryFn: () => api.get('/api/imports'),
  });

  const preview = useMutation<{
    rowsTotal: number;
    rowsOk: number;
    rowsFailed: number;
    errors: Array<{ row: number; error: string }>;
    sample: unknown[];
  }>({
    mutationFn: () => api.post('/api/imports/preview', { kind, content }),
    onError: (err: Error) => setError(err.message),
    onSuccess: () => setError(null),
  });

  const commit = useMutation({
    mutationFn: () => api.post('/api/imports/commit', { kind, content }),
    onSuccess: () => {
      setError(null);
      setAsking(false);
      setContent('');
      preview.reset();
      void client.invalidateQueries({ queryKey: ['imports'] });
    },
    onError: (err: Error) => {
      setAsking(false);
      setError(err.message);
    },
  });

  const hasContent = content.trim().length > 0;

  return (
    <>
      <PageHeader
        level={2}
        title="Import dat ze souboru"
        description="Nahrání závodů, objednávek nebo zákazníků z CSV či JSON. Jde stejnou cestou jako události z e-shopu, takže výsledek je stejný."
      />
      <Steps
        label="Postup importu ze souboru"
        steps={[
          {
            title: 'Vložte data',
            text: 'Vyberte druh dat a vložte obsah souboru. Šablonu si můžete stáhnout.',
            state: hasContent || preview.data ? 'done' : 'now',
          },
          {
            title: 'Zkouška bez zápisu',
            text: 'Hub data zkontroluje a ukáže chyby po řádcích. Nic se nezapíše.',
            state: preview.data ? 'done' : hasContent ? 'now' : 'todo',
          },
          {
            title: 'Ostrý zápis',
            text: 'Po vaší kontrole se data zapíšou do hubu a přepočítají se cesty.',
            state: preview.data ? 'now' : 'todo',
          },
        ]}
      />
      {error ? <ErrorState error={new Error(error)} title="Import se nepovedl" /> : null}
      <Panel title="1. Data k importu">
        <div className="s2c-form">
          <Field label="Druh dat">
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              {(list.data?.kinds ?? ['races']).map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k] ?? k}
                </option>
              ))}
            </select>
          </Field>
          <div>
            <a className="btn" href={`/api/imports/template/${kind}`}>
              Stáhnout šablonu
            </a>
          </div>
          <div className="wide">
            <Field label="Obsah souboru" hint="Vložte obsah CSV nebo JSON.">
              <textarea
                className="s2c-textarea"
                value={content}
                disabled={!canWrite}
                rows={10}
                onChange={(e) => setContent(e.target.value)}
              />
            </Field>
          </div>
        </div>
        <div className="s2c-actions">
          <button
            type="button"
            className="btn pri"
            disabled={!canWrite || !hasContent || preview.isPending}
            onClick={() => preview.mutate()}
          >
            {preview.isPending ? 'Kontroluji…' : '2. Zkouška bez zápisu'}
          </button>
          {!canWrite ? <span className="note">Import smí spustit jen správce.</span> : null}
        </div>
      </Panel>

      {preview.data ? (
        <Panel
          title="Výsledek zkoušky"
          subtitle="Zatím se nic nezapsalo. Zkontrolujte počty a teprve potom potvrďte zápis."
          action={
            preview.data.rowsFailed > 0 ? (
              <StatusBadge tone="warn" icon="alert">
                {num(preview.data.rowsFailed)} řádků s chybou
              </StatusBadge>
            ) : (
              <StatusBadge tone="ok" icon="check">
                bez chyb
              </StatusBadge>
            )
          }
        >
          <div className="tiles">
            <Tile label="Řádků celkem" value={num(preview.data.rowsTotal)} />
            <Tile label="V pořádku" value={num(preview.data.rowsOk)} />
            <Tile
              label="S chybou"
              value={num(preview.data.rowsFailed)}
              bad={preview.data.rowsFailed > 0}
            />
          </div>
          {preview.data.errors.length > 0 ? (
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th className="n">Řádek</th>
                    <th>Chyba</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.data.errors.map((e, i) => (
                    <tr key={i}>
                      <td className="n">{e.row}</td>
                      <td className="muted">{e.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <details className="s2c-details">
            <summary>První načtené záznamy</summary>
            <pre className="code">{JSON.stringify(preview.data.sample, null, 2)}</pre>
          </details>
          <div className="s2c-actions">
            <button
              type="button"
              className="btn pri"
              disabled={!canWrite || commit.isPending}
              onClick={() => setAsking(true)}
            >
              3. Potvrdit ostrý zápis ({num(preview.data.rowsOk)} řádků)
            </button>
          </div>
        </Panel>
      ) : null}

      <Panel title="Historie importů" subtitle="Poslední importy ze souborů v tomto prostředí.">
        {list.isLoading ? <LoadingState what="historii" /> : null}
        {list.isError ? (
          <ErrorState
            error={list.error}
            title="Historii se nepodařilo načíst"
            onRetry={() => void list.refetch()}
          />
        ) : null}
        {list.data && list.data.items.length === 0 ? (
          <EmptyState title="Zatím žádný import">
            Až nějaký provedete, objeví se tady s počty řádků.
          </EmptyState>
        ) : null}
        {list.data && list.data.items.length > 0 ? (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Kdy</th>
                  <th>Druh</th>
                  <th>Soubor</th>
                  <th className="n">Řádků</th>
                  <th className="n">V pořádku</th>
                  <th className="n">Chyb</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((i) => (
                  <tr key={i.id}>
                    <td>{dateTime(i.at)}</td>
                    <td>{KIND_LABEL[i.kind] ?? i.kind}</td>
                    <td>{i.filename ?? '—'}</td>
                    <td className="n">{num(i.rows_total)}</td>
                    <td className="n">{num(i.rows_ok)}</td>
                    <td className="n">{num(i.rows_failed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Panel>

      <ConfirmDialog
        open={asking}
        title="Zapsat import do hubu?"
        confirmLabel="Zapsat do hubu"
        busy={commit.isPending}
        onCancel={() => setAsking(false)}
        onConfirm={() => commit.mutate()}
      >
        <p>
          Import zapíše {preview.data ? num(preview.data.rowsOk) : ''} řádků (
          {KIND_LABEL[kind] ?? kind}) do databáze tohoto prostředí a přepočítá cesty. Žádná zpráva
          se tím neodešle.
        </p>
      </ConfirmDialog>
    </>
  );
}

function UsersTab({ canManage }: { canManage: boolean }): JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState<{ id: string; email: string } | null>(null);
  const [form, setForm] = useState<{
    email: string;
    name: string;
    password: string;
    departments: string[];
  }>({ email: '', name: '', password: '', departments: ['obchod'] });

  const list = useQuery<{
    items: Array<{
      id: string;
      email: string;
      name: string;
      departments: string[];
      active: boolean;
    }>;
    departments: string[];
  }>({
    queryKey: ['users'],
    queryFn: () => api.get('/api/users'),
    enabled: canManage,
  });

  const create = useMutation({
    mutationFn: () => api.post('/api/users', form),
    onSuccess: () => {
      setError(null);
      setForm({ email: '', name: '', password: '', departments: ['obchod'] });
      void client.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const update = useMutation({
    mutationFn: (input: { id: string; departments?: string[]; active?: boolean }) =>
      api.patch(`/api/users/${input.id}`, input),
    onSuccess: () => {
      setError(null);
      setDeactivating(null);
      void client.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: Error) => {
      setDeactivating(null);
      setError(err.message);
    },
  });
  const resetMfa = useMutation({
    mutationFn: (id: string) => api.post(`/api/users/${id}/reset-mfa`),
    onSuccess: () => setError(null),
    onError: (err: Error) => setError(err.message),
  });

  if (!canManage)
    return (
      <EmptyState title="Uživatele spravuje administrátor" icon="users">
        Pro správu účtů a oddělení požádejte administrátora.
      </EmptyState>
    );
  if (list.isLoading) return <LoadingState what="uživatele" />;
  if (list.isError)
    return (
      <ErrorState
        error={list.error}
        title="Uživatele se nepodařilo načíst"
        onRetry={() => void list.refetch()}
      />
    );

  const toggle = (current: string[], code: string): string[] =>
    current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
  const passwordShort = form.password.length > 0 && form.password.length < 10;

  return (
    <>
      {error ? <ErrorState error={new Error(error)} title="Změna se nepovedla" /> : null}
      <Panel
        title="Uživatelé"
        subtitle="Práva uživatele jsou sjednocením práv jeho oddělení. Uživatel musí mít aspoň jedno oddělení."
      >
        {list.data && list.data.items.length === 0 ? (
          <EmptyState title="Žádní uživatelé" icon="users">
            Založte prvního uživatele ve formuláři níže.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>E-mail</th>
                  <th>Jméno</th>
                  <th>Oddělení</th>
                  <th>Stav</th>
                </tr>
              </thead>
              <tbody>
                {list.data?.items.map((u) => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td>{u.name}</td>
                    <td>
                      {DEPARTMENTS.map((d) => (
                        <label key={d} className="s2c-nowrap">
                          <input
                            type="checkbox"
                            checked={u.departments.includes(d)}
                            aria-label={`${DEPARTMENT_LABEL[d]} — ${u.email}`}
                            onChange={() => {
                              const next = toggle(u.departments, d);
                              if (next.length > 0) update.mutate({ id: u.id, departments: next });
                            }}
                          />{' '}
                          {DEPARTMENT_LABEL[d]}{' '}
                        </label>
                      ))}
                    </td>
                    <td>
                      {u.active ? (
                        <>
                          <StatusBadge tone="ok" icon="check">
                            aktivní
                          </StatusBadge>{' '}
                          <button
                            type="button"
                            className="btn"
                            onClick={() => setDeactivating({ id: u.id, email: u.email })}
                          >
                            Deaktivovat
                          </button>{' '}
                          <button
                            type="button"
                            className="btn"
                            title="Smaže uživateli dvoufázové přihlášení, záložní kódy i passkey (ztracený telefon); zapíše se do auditu"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Resetovat dvoufázové přihlášení a passkey uživatele ${u.email}?`,
                                )
                              )
                                resetMfa.mutate(u.id);
                            }}
                          >
                            Resetovat 2FA
                          </button>
                        </>
                      ) : (
                        <>
                          <StatusBadge tone="neu">neaktivní</StatusBadge>{' '}
                          <button
                            type="button"
                            className="btn"
                            onClick={() => update.mutate({ id: u.id, active: true })}
                          >
                            Aktivovat
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Nový uživatel" subtitle="Účet vznikne jen v tomto prostředí.">
        <div className="s2c-form">
          <Field label="E-mail">
            <input
              type="email"
              autoComplete="off"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field label="Jméno">
            <input
              type="text"
              autoComplete="off"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field
            label="Heslo"
            hint="Aspoň 10 znaků. Po založení se už nezobrazí."
            error={passwordShort ? 'Heslo je příliš krátké.' : null}
          >
            <input
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </Field>
        </div>
        <fieldset className="s2c-fieldset">
          <legend>Oddělení</legend>
          <div className="row">
            {DEPARTMENTS.map((d) => (
              <label key={d} className="s2c-nowrap">
                <input
                  type="checkbox"
                  checked={form.departments.includes(d)}
                  onChange={() => setForm({ ...form, departments: toggle(form.departments, d) })}
                />{' '}
                {DEPARTMENT_LABEL[d]}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="s2c-actions">
          <button
            type="button"
            className="btn pri"
            disabled={form.departments.length === 0 || create.isPending}
            onClick={() => create.mutate()}
          >
            Založit uživatele
          </button>
        </div>
      </Panel>

      <ConfirmDialog
        open={deactivating !== null}
        title="Deaktivovat uživatele?"
        confirmLabel="Deaktivovat"
        danger
        busy={update.isPending}
        onCancel={() => setDeactivating(null)}
        onConfirm={() => {
          if (deactivating) update.mutate({ id: deactivating.id, active: false });
        }}
      >
        <p>
          Uživatel <b>{deactivating?.email}</b> se přestane moct přihlásit. Jeho data a historie
          zůstanou; znovu ho lze aktivovat.
        </p>
      </ConfirmDialog>
    </>
  );
}

function AuditTab({ me }: { me: Me }): JSX.Element {
  const query = useQuery<{
    items: Array<{
      id: number;
      action: string;
      entity: string;
      entityId: string | null;
      before: unknown;
      after: unknown;
      at: string;
      userEmail: string | null;
    }>;
  }>({
    queryKey: ['audit'],
    queryFn: () => api.get('/api/audit?limit=200'),
  });

  if (query.isLoading) return <LoadingState what="audit" />;
  if (query.isError)
    return (
      <ErrorState
        error={query.error}
        title="Audit se nepodařilo načíst"
        onRetry={() => void query.refetch()}
      />
    );

  return (
    <Panel
      title="Audit změn"
      subtitle="Každá změna dat v administraci se zapisuje se stavem před a po. Zobrazeno posledních 200."
    >
      {query.data && query.data.items.length === 0 ? (
        <EmptyState title="Zatím žádná změna">
          Jakmile někdo něco v administraci změní, objeví se to tady.
        </EmptyState>
      ) : (
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Kdy</th>
                <th>Kdo</th>
                <th>Akce</th>
                <th>Co</th>
                <th>Změna</th>
              </tr>
            </thead>
            <tbody>
              {query.data?.items.map((a) => (
                <tr key={a.id}>
                  <td>{dateTime(a.at, me.defaultTimezone)}</td>
                  <td>{a.userEmail ?? <span className="muted">systém</span>}</td>
                  <td>
                    <code>{a.action}</code>
                  </td>
                  <td>
                    {a.entity}
                    {a.entityId ? (
                      <div className="muted">
                        <code>{a.entityId}</code>
                      </div>
                    ) : null}
                  </td>
                  <td className="muted s2c-wrapcell">
                    {a.after ? JSON.stringify(a.after).slice(0, 200) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
